/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type MediaSharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import type MediaSource from '@linc-technologies/linced-common/enums/media-source';
import type MediaType from '@linc-technologies/linced-common/enums/media-type';
import type Image from '@linc-technologies/linced-common/models/media/image';
import type Rendition from '@linc-technologies/linced-common/models/media/rendition';
import type Video from '@linc-technologies/linced-common/models/media/video';
import type Person from '@linc-technologies/linced-common/models/person';
import type AttendanceNote from '@linc-technologies/linced-common/models/posts/attendancenote';
import type GlobalNotice from '@linc-technologies/linced-common/models/posts/globalnotice';
import type Post from '@linc-technologies/linced-common/models/posts/post';
import type School from '@linc-technologies/linced-common/models/school';
import config from '@linc-technologies/linced-common/utils/config';

// Proto: media.MediaMeta.
export default class Media extends Model {
  declare [Type]: 'media';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>; // This is uploader id

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @belongsTo('posts/post', { async: true, inverse: 'mediaIds' })
  declare postId: AsyncBelongsTo<Post>;

  @alias('postId')
  declare post: AsyncBelongsTo<Post>;

  @attr('number', { defaultValue: 0 })
  declare sharedWith: EnumValue<typeof MediaSharingLevel>;

  @attr('number', { defaultValue: 0 })
  declare mediaType: EnumValue<typeof MediaType>;

  @attr('number', { defaultValue: 0 })
  declare mediaSource: EnumValue<typeof MediaSource>;

  @attr('string', { defaultValue: '' })
  declare mimeType: string;

  @attr('string', { defaultValue: '' })
  declare fileName: string;

  @attr('string', { defaultValue: '' })
  declare storageKey: string;

  @attr('string', { defaultValue: '' })
  declare resourceKey: string;

  @attr('string', { defaultValue: '' })
  declare icon: string;

  @attr('number', { defaultValue: 0 })
  declare fileSize: number;

  @attr('string', { defaultValue: '' })
  declare altText: string;

  @fragmentArray('media/rendition')
  declare renditions: FragmentArray<Rendition>;

  @fragment('media/image')
  declare image: Image | null;

  @fragment('media/video')
  declare video: Video | null;

  @attr('boolean')
  declare canReplace: boolean | undefined;

  @attr('string', { defaultValue: '' })
  declare thumbnailUrl: string;

  @belongsTo('posts/attendancenote', { async: true, inverse: 'mediaIds' })
  declare attendanceNoteId: AsyncBelongsTo<AttendanceNote>;

  @belongsTo('posts/globalnotice', { async: true, inverse: 'mediaIds' })
  declare globalNoticeId: AsyncBelongsTo<GlobalNotice>;

  @computed('fileName')
  get decodedFileName() {
    try {
      return decodeURIComponent(this.fileName);
    } catch {
      return this.fileName;
    }
  }

  // rawUrl provides a direct link to the underlying media. This is required for
  // attachments where we need to bounce users out from the app to a web
  // browser.
  // With iOS, the web cache is not shared between apps and safari, therefore
  // the blob stored within the app doesn't exist in Safari, so we need to pull
  // it again in the browser.
  // https://linced.atlassian.net/browse/HERO-11042
  @computed('id', 'fileName')
  get rawUrl() {
    return `${
      config.APP.apiHost
    }/media/${config.APP.apiPath}/media/${this.id}/${encodeURIComponent(this.fileName)}`;
  }
}
